import type { ReactNode } from 'react';
import {
  Box,
  Stack,
  Text,
  Tabs,
  PersonIcon,
  LocationIcon,
  DeliveryIcon,
  type TabItem,
} from '@citibox-panel/ui';
import {
  ROOT_SUBTAB,
  deliveryIdFromTabKey,
  isLocationTab,
  type SearchTab,
} from '../model/shellState';

// Abbreviate long opaque ids the way the design does: a3f1…d740.
function shortId(id: string): string {
  return id.length > 9 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

// A sub-tab label: a leading icon and a single-line, ellipsis-truncated caption. The text
// is width-capped so a long label (e.g. a delivery tracking) never grows the tab past its
// close affordance — the "Cerrar" control stays visible.
function subTabLabel(icon: ReactNode, text: string): ReactNode {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      {icon}
      <Box sx={{ maxWidth: 160 }}>
        <Text variant="body2" noWrap>
          {text}
        </Text>
      </Box>
    </Stack>
  );
}

export interface EntitySubTabsProps {
  search: SearchTab;
  onSelectSubtab: (id: string) => void;
  onCloseDelivery: (id: string) => void;
}

export function EntitySubTabs({ search, onSelectSubtab, onCloseDelivery }: EntitySubTabsProps) {
  const items: TabItem[] = [
    {
      value: ROOT_SUBTAB,
      // The predicate, not a bare `kind` check: inside it `label` is a `string`, which is what
      // `subTabLabel` takes — so the address needs no fallback, and the guarantee is carried by
      // the same function normalization filters on rather than by a comment.
      // TODO: i18n — copy en ES hasta Fase 1.
      label: isLocationTab(search)
        ? subTabLabel(<LocationIcon fontSize="small" />, search.label)
        : // A loose delivery tab IS the delivery, so its root pill takes the delivery glyph and the
          // tracking that opened it. The person glyph here would promise a ficha nothing can render.
          search.kind === 'delivery'
          ? subTabLabel(
              <DeliveryIcon fontSize="small" />,
              search.label ?? `Entrega ${shortId(deliveryIdFromTabKey(search.key))}`,
            )
          : subTabLabel(<PersonIcon fontSize="small" />, search.label ?? 'Usuario'),
    },
    ...search.deliveries.map((delivery) => ({
      value: delivery.id,
      closable: true,
      // TODO: i18n — copy en ES hasta Fase 1.
      label: subTabLabel(
        <DeliveryIcon fontSize="small" />,
        delivery.label ?? `Entrega ${shortId(delivery.id)}`,
      ),
    })),
  ];

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        items={items}
        value={search.activeSubtab}
        onChange={onSelectSubtab}
        variant="pill"
        onClose={onCloseDelivery}
      />
    </Box>
  );
}
